import * as React from 'react';
import withStyles, { WithStylesProps } from 'react-jss';
import { CssType, ThemeType } from 'src/theming/jssTypes';
import { Upload, Button } from 'src/design-system/components/atoms';
import { handleXLSXFile, handleXLSXFileDateTime, handleXLSXWorkbook, handleXLSXFileWithoutMapping,
  handleXLSXWorkbookWithoutMapping } from 'src/utils/utils';
import { UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
const uploadIcon = require('src/assets/images/upload.svg');
import classNames from 'classnames';
import moment from 'moment';
import { withTranslation, WithTranslation } from 'react-i18next';

const Dragger = Upload.Dragger;

type CustomerRequestArgs = {
  onSuccess?: any;
  onError?: any;
  file?: any;
};

const styles = (theme: ThemeType): CssType => ({
  link: {
    color: theme.color.semantic.brand.default.primary,
    cursor: 'pointer',
  },
  disabled: {
    opacity: theme.opacity[50],
    pointerEvents: 'none',
  },
  downloadDiv: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing[2],
    '& a': {
      color: theme.color.semantic.brand.default.primary
    },
  },
  dragger: {
    padding: theme.spacing[4],
  },
  uploadHint: {
    marginBottom: theme.spacing[5],
    fontSize: theme.typography.fontSize.md,
  },
  uploadText: {
    color: theme.color.semantic.text.secondary,
    marginBottom: theme.spacing[8],
    fontSize: theme.typography.fontSize.md,
    fontWeight: theme.typography.fontWeight.regular,
  },
  linkText: {
    color: theme.color.semantic.brand.default.primary,
  },
});

export interface ExcelUploadProps extends WithTranslation, WithStylesProps<typeof styles> {
  mapping?: any;
  onDataParsed?: (data: any, file?: any) => void;
  dateTime?: boolean;
  multipleSheets?: boolean;
  multipleMappings?: any;
  firstRowAsHeader?: boolean;
  enableHeaderMapping?: boolean;
  useRawParsing?: boolean;
  sampleLink?: string;
  sampleApi?: () => void;
  showButtonOnly?: boolean;
  isDisabled?: boolean;
  title?: string;
  additionalUploadParams?: any;
  isMultipleImageUpload?: boolean;
}

interface ExcelUploadState {
  fileList: UploadFile[];
}

class CustomExcelUpload extends React.Component<ExcelUploadProps, ExcelUploadState> {
  temp: UploadFile[] = [];

  constructor(props: ExcelUploadProps) {
    super(props);
    this.state = {
      fileList: this.temp,
    };
  }

  handleMultipleImageUpload = (info: UploadChangeParam) => {
    const { onDataParsed } = this.props;
    this.setState({
      fileList: info.fileList,
    });

    if (this.state.fileList && this.state.fileList.length) {
      const { fileList } = this.state;
      onDataParsed?.(fileList);
    }
  };

  handleUploadChange = (info: UploadChangeParam) => {
    const status = info.file.status;
    if (status === 'done') {
      this.onFileRecieved(info.file.originFileObj);
    }
    this.setState({
      fileList: [info.file],
    });
  };

  customRequest = (args: CustomerRequestArgs) => {
    const { onSuccess, file } = args;
    setTimeout(() => {
      onSuccess(null, file);
    }, 100);
  };

  onFileRecieved = async (file: any) => {
    const { mapping, onDataParsed, dateTime, multipleSheets, multipleMappings,
      firstRowAsHeader, enableHeaderMapping, useRawParsing } = this.props;

    const header_val = enableHeaderMapping ? 1 : 0;
    const range_val = enableHeaderMapping ? 1 : 0;
    if (multipleSheets) {
      const dataListArray = multipleMappings ? await handleXLSXWorkbook(file, multipleMappings) :
        await handleXLSXWorkbookWithoutMapping(file, header_val, range_val);
      if (dataListArray?.Consignments) {
        dataListArray.Consignments = dataListArray.Consignments?.map((data: any) => {
          if (data?.invoice_date && moment(data?.invoice_date).isValid()) {
            return {
              ...data,
              invoice_date: moment(data.invoice_date).add(1, 'h').toISOString(),
            };
          }
          return data;
        });
      }
      if (onDataParsed) {
        onDataParsed(dataListArray, file);
      }
    } else {
      let dataList: any = [];
      if(mapping){
        dataList = dateTime ?
        await handleXLSXFileDateTime(file,  Object.keys(mapping)) :
        await handleXLSXFile(file, Object.keys(mapping));
        // Skip header row when mapping is provided
        // The first row in CSV is always headers, but XLSX may include it as data
        if (dataList.length > 0) {
          const headerValues = Object.values(mapping);
          const firstRow = dataList[0];
          const isHeaderRow = Object.keys(firstRow).some(key =>
            headerValues.includes(firstRow[key])
          );
          if (isHeaderRow) {
            dataList = dataList.slice(1);
          }
        }
      }else {
        dataList = dateTime ?
        await handleXLSXFileDateTime(file, firstRowAsHeader ? 0 : 1, 0) :
        await handleXLSXFileWithoutMapping(file, firstRowAsHeader, useRawParsing);
      }
      if (onDataParsed) {
        onDataParsed(dataList, file);
      }
    }
  };

  uploadProps = {
    accept: '.xlsx, .xls, .csv',
    name: 'file',
    multiple: false,
    customRequest: this.customRequest,
    showUploadList:{ showPreviewIcon: false, showRemoveIcon: false },
  };

  render() {
    const { sampleLink, sampleApi, classes, showButtonOnly, isDisabled,
      title, additionalUploadParams, t, isMultipleImageUpload = false } = this.props;
    const { fileList } = this.state;
    this.uploadProps = {...this.uploadProps, ...additionalUploadParams};

    return (
      <div>
        {showButtonOnly ? (<Upload
          fileList={fileList}
          onChange={this.handleUploadChange}
          {...this.uploadProps}
        >
          <Button>{title ? title : t('upload')}</Button>
        </Upload>) : ( <Dragger
          fileList={fileList}
          className={classes.dragger}
          onChange={isMultipleImageUpload? this.handleMultipleImageUpload :this.handleUploadChange}
          {...this.uploadProps}
          >
          <p className={classNames("ant-upload-hint", classes.uploadHint)}>
          </p>
          <p className="ant-upload-drag-icon">
            <img style={{ height: '3em' }} src={uploadIcon} />
          </p>
          <p className={classes.uploadText}>
            <b>{t('Drag and drop to upload')} <br/> {t('or')}
            <span className={classes.linkText}> {t('browse')}</span> {t('to choose file')}</b>
          </p>
        </Dragger>)}
        {sampleLink &&
        <div className = { isDisabled ? classNames(classes.disabled, classes.downloadDiv)
          : classes.downloadDiv}>
          <a href={sampleLink} target={'_blank'}>
            {t('download_a_sample_file')}
           </a>
        </div>}
        {sampleApi &&
          <div className = { isDisabled ? classNames(classes.disabled, classes.downloadDiv)
            : classes.downloadDiv}>
          <span className ={classes.link} onClick = {() => sampleApi()} >
          {t('download_a_sample_file')}
           </span>
        </div>
        }
      </div>
    );
  }
}

const ExcelUploadWithTranslation = withTranslation('translation')(CustomExcelUpload);
const ExcelUpload = withStyles(styles, { injectTheme: true })(ExcelUploadWithTranslation);

export default ExcelUpload;
